'use client';

import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Feature {
  label: string;
}

/** One carousel slide. */
export interface HeroSlide {
  eyebrow?: string;
  title: ReactNode;
  subtitle?: string;
  description?: string;
  /** Slide image. Omit to fall back to the section-level `imageSrc`. */
  imageSrc?: string;
  imageAlt?: string;
}

interface HeroSectionProps {
  title: ReactNode;
  features?: Feature[];
  subtitle?: string;
  description?: string;
  primaryButtonText?: string;
  primaryButtonHref?: string;
  secondaryButtonText?: string;
  secondaryButtonHref?: string;
  imageSrc: string;
  imageAlt?: string;
  /**
   * Optional carousel. With two or more slides the hero rotates through them;
   * with none it renders the single-slide hero exactly as before, which is what
   * /about relies on. `title`/`subtitle`/`description` are ignored when set.
   */
  slides?: HeroSlide[];
  /**
   * Full-bleed "cover" layout: the slide image spans the whole hero and the
   * copy + CTAs sit overlaid on a legibility scrim (homepage). Default false
   * keeps the original split layout (text beside a contained image).
   */
  overlay?: boolean;
}

/** Renders as the given tag  - lets the active slide be the page's only <h1>. */
function Heading({
  as: Tag,
  className,
  children,
}: {
  as: 'h1' | 'p';
  className?: string;
  children: ReactNode;
}) {
  return <Tag className={className}>{children}</Tag>;
}

/**
 * Cross-fade applied to BOTH the text stack and the image stack.
 *
 * They must run on the same clock. They were previously 500ms and 700ms, which
 * left a ~200ms window on every change where the incoming text was fully opaque
 * over the outgoing slide's photo  - it read as "slide 3 is showing slide 2's
 * image". Change the duration here or not at all.
 */
const SLIDE_FADE = 'transition-opacity duration-500 ease-organic motion-reduce:transition-none';

/** How long each slide holds before advancing. */
const SLIDE_MS = 7000;

export default function HeroSection({
  title,
  features,
  subtitle,
  description,
  // CTAs render in the `overlay` layout; the split layout keeps them hidden
  // (the originals are commented out below for a future release).
  primaryButtonText = 'Explore Products',
  primaryButtonHref = '/products',
  secondaryButtonText = '',
  secondaryButtonHref = '/contact',
  imageSrc = '/assets/images/homeNew.jpg',
  imageAlt = 'Elegant sustainable table setting with Flora Dine bagasse plates, bowls, and fresh natural food',
  slides,
  overlay = false,
}: HeroSectionProps) {
  // Fall back to the single-slide hero so existing callers are untouched.
  const items: HeroSlide[] =
    slides && slides.length > 0 ? slides : [{ title, subtitle, description }];
  const isCarousel = items.length > 1;

  const [index, setIndex] = useState(0);
  // Auto-rotation is a WCAG 2.2.2 concern: anything that moves for more than
  // five seconds needs a way to stop it. Hence the visible pause control, the
  // pause-on-hover/focus below, and the reduced-motion opt-out.
  const [isPlaying, setIsPlaying] = useState(true);
  const [interacted, setInteracted] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  /**
   * Which slide images to mount. All the layers sit inside the viewport, so
   * `loading="lazy"` defers nothing  - without this the page downloads every
   * slide's photo on first paint in order to show one of them.
   *
   * Derived from `index` rather than accumulated in state: keeping a Set in
   * state meant setting state from an effect, which triggers a cascading
   * render. The neighbours are enough  - the outgoing slide during a fade is
   * always `index - 1`, so it stays mounted for the whole crossfade, and a
   * remount later is served from the browser cache rather than the network.
   */
  const shouldMount = (i: number) =>
    i === index ||
    i === (index + 1) % items.length ||
    i === (index - 1 + items.length) % items.length;

  const go = useCallback(
    (next: number) => setIndex(((next % items.length) + items.length) % items.length),
    [items.length]
  );
  const next = useCallback(() => go(index + 1), [go, index]);
  const prev = useCallback(() => go(index - 1), [go, index]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReducedMotion(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  // Advance on a timer. Skipped entirely when paused, when the user prefers
  // reduced motion, or when only one slide exists.
  useEffect(() => {
    if (!isCarousel || !isPlaying || reducedMotion) return;
    const id = setTimeout(() => go(index + 1), SLIDE_MS);
    return () => clearTimeout(id);
  }, [isCarousel, isPlaying, reducedMotion, index, go]);

  // Don't animate in a background tab  - it burns a timer and the user returns
  // to a slide they never saw start.
  useEffect(() => {
    const onVisibility = () => setIsPlaying(!document.hidden && !interacted);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [interacted]);

  // Manual control stops the rotation for good  - re-starting under someone who
  // just clicked "previous" is the classic carousel annoyance.
  const takeControl = (fn: () => void) => () => {
    setInteracted(true);
    setIsPlaying(false);
    fn();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      takeControl(next)();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      takeControl(prev)();
    }
  };

  // Touch swipe.
  const touchX = useRef<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    touchX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) takeControl(dx < 0 ? next : prev)();
    touchX.current = null;
  };

  const controlBtn =
    'flex h-11 w-11 items-center justify-center rounded-full border border-border bg-surface/90 text-foreground shadow-subtle backdrop-blur transition duration-200 hover:border-primary/40 hover:text-primary active:scale-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary';

  // Light-on-dark control button for the cover layout (sits over the photo scrim).
  const coverControlBtn =
    'flex h-11 w-11 items-center justify-center rounded-full border border-primary-foreground/40 bg-primary-foreground/10 text-primary-foreground backdrop-blur transition duration-200 hover:bg-primary-foreground/20 active:scale-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-foreground';

  // ── Full-bleed cover layout (homepage) ───────────────────────────────────
  if (overlay) {
    const hasPrimary = primaryButtonHref.length > 1 && primaryButtonText.length > 0;
    const hasSecondary = secondaryButtonHref.length > 1 && secondaryButtonText.length > 0;
    return (
      <section
        className="relative isolate flex h-[86vh] max-h-[760px] min-h-[600px] items-center overflow-hidden"
        onTouchStart={isCarousel ? onTouchStart : undefined}
        onTouchEnd={isCarousel ? onTouchEnd : undefined}
        {...(isCarousel
          ? {
              'aria-roledescription': 'carousel',
              'aria-label': 'Flora Dine highlights',
              onMouseEnter: () => setIsPlaying(false),
              onMouseLeave: () => !interacted && setIsPlaying(true),
              onFocus: () => setIsPlaying(false),
              onKeyDown,
            }
          : {})}
      >
        {/* Cover images cross-fade behind the copy. */}
        {items.map((slide, i) => {
          const active = i === index;
          return (
            <div
              key={i}
              aria-hidden={isCarousel && !active}
              className={`absolute inset-0 -z-10 ${SLIDE_FADE} ${active ? 'opacity-100' : 'opacity-0'}`}
            >
              {shouldMount(i) && (
                <AppImage
                  src={slide.imageSrc ?? imageSrc}
                  alt={slide.imageAlt ?? imageAlt}
                  fill
                  priority={i === 0}
                  loading={i === 0 ? undefined : 'lazy'}
                  sizes="100vw"
                  className="h-full w-full object-cover object-center"
                />
              )}
            </div>
          );
        })}
        {/* Legibility scrim: darker on the left for the copy, soft band at the base. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-gradient-to-r from-foreground/85 via-foreground/55 to-foreground/15"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-gradient-to-t from-foreground/50 via-transparent to-transparent"
        />

        <div className="mx-auto w-full max-w-7xl px-4 pt-16 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div
              className="grid"
              aria-live={isCarousel && isPlaying && !reducedMotion ? 'off' : 'polite'}
              aria-atomic="false"
            >
              {items.map((slide, i) => {
                const active = i === index;
                return (
                  <div
                    key={i}
                    className={`col-start-1 row-start-1 ${SLIDE_FADE} ${
                      active ? 'opacity-100' : 'pointer-events-none opacity-0'
                    }`}
                    {...(isCarousel
                      ? {
                          'aria-roledescription': 'slide',
                          'aria-label': `${i + 1} of ${items.length}`,
                          'aria-hidden': !active,
                          inert: !active,
                        }
                      : {})}
                  >
                    {slide.eyebrow && (
                      <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary-foreground/25 bg-primary-foreground/10 px-4 py-2 font-cta text-xs font-semibold uppercase tracking-wider text-primary-foreground backdrop-blur-sm">
                        {slide.eyebrow}
                      </span>
                    )}
                    <Heading
                      as={active ? 'h1' : 'p'}
                      className="font-headline text-3xl font-extrabold leading-[1.1] tracking-tight text-primary-foreground sm:text-4xl lg:text-5xl"
                    >
                      {slide.title}
                      {slide.subtitle && (
                        <>
                          {' '}
                          <span className="text-secondary">{slide.subtitle}</span>
                        </>
                      )}
                    </Heading>
                    {slide.description && (
                      <p className="mt-5 max-w-xl font-body text-base leading-relaxed text-primary-foreground/85 sm:text-lg">
                        {slide.description}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {(hasPrimary || hasSecondary) && (
              <div className="mt-8 flex flex-wrap gap-3">
                {hasPrimary && (
                  <Link
                    href={primaryButtonHref}
                    className="group inline-flex min-h-12 items-center gap-2 rounded-xl bg-secondary px-6 py-3 font-cta text-base font-bold text-primary shadow-subtle transition duration-200 hover:-translate-y-0.5 hover:bg-secondary/90 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 focus-visible:ring-offset-foreground"
                  >
                    {primaryButtonText}
                    <Icon
                      name="ArrowRightIcon"
                      size={18}
                      className="transition-transform duration-200 group-hover:translate-x-1"
                      aria-hidden="true"
                    />
                  </Link>
                )}
                {hasSecondary && (
                  <Link
                    href={secondaryButtonHref}
                    className="inline-flex min-h-12 items-center gap-2 rounded-xl border border-primary-foreground/40 bg-primary-foreground/10 px-6 py-3 font-cta text-base font-bold text-primary-foreground backdrop-blur-sm transition duration-200 hover:bg-primary-foreground/20 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-foreground"
                  >
                    {secondaryButtonText}
                  </Link>
                )}
              </div>
            )}

            {isCarousel && (
              <div className="mt-10 flex items-center gap-4">
                <div className="flex items-center gap-2" role="group" aria-label="Choose slide">
                  {items.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={takeControl(() => go(i))}
                      aria-label={`Go to slide ${i + 1}`}
                      aria-current={i === index}
                      className={`h-2.5 rounded-full transition-all duration-300 ${
                        i === index
                          ? 'w-7 bg-secondary'
                          : 'w-2.5 bg-primary-foreground/50 hover:bg-primary-foreground/80'
                      }`}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={takeControl(prev)} className={coverControlBtn}>
                    <Icon name="ArrowLeftIcon" size={18} aria-hidden="true" />
                    <span className="sr-only">Previous slide</span>
                  </button>
                  <button type="button" onClick={takeControl(next)} className={coverControlBtn}>
                    <Icon name="ArrowRightIcon" size={18} aria-hidden="true" />
                    <span className="sr-only">Next slide</span>
                  </button>
                  {!reducedMotion && (
                    <button
                      type="button"
                      onClick={() => {
                        setInteracted(isPlaying);
                        setIsPlaying((prevPlaying) => !prevPlaying);
                      }}
                      className="sr-only focus:not-sr-only focus:flex focus:h-11 focus:w-auto focus:items-center focus:rounded-full focus:border focus:border-primary-foreground/40 focus:bg-foreground/60 focus:px-4 focus:font-cta focus:text-sm focus:text-primary-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-foreground"
                    >
                      {isPlaying ? 'Pause slideshow' : 'Play slideshow'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="relative overflow-hidden pt-10"
      {...(isCarousel
        ? {
            'aria-roledescription': 'carousel',
            'aria-label': 'Flora Dine highlights',
            onMouseEnter: () => setIsPlaying(false),
            onMouseLeave: () => !interacted && setIsPlaying(true),
            onFocus: () => setIsPlaying(false),
            onKeyDown,
          }
        : {})}
    >
      {/*
        Text AND image share the SAME container as every other section
        (`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8`), as a two-column grid, so both
        gutters match the rest of the page by construction. Two earlier attempts
        did not: deriving the left offset from a 100vw calc sat 7px off (100vw
        includes the scrollbar, a centred container does not), and pinning the
        image absolutely to the right edge left a 105px gutter on the left
        against 0px on the right.
      */}
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="lg:grid lg:min-h-[32rem] lg:grid-cols-2 lg:items-center lg:gap-10">
          <div className="flex flex-col justify-center py-10 lg:py-16">
            {/*
          All slides occupy the same grid cell so the container sizes to the
          tallest one. Without this the section height jumps as slides of
          different length rotate through.
        */}
            <div
              className="grid"
              // APG: keep the live region quiet while it auto-rotates, and announce
              // changes once the user is driving.
              aria-live={isCarousel && isPlaying && !reducedMotion ? 'off' : 'polite'}
              aria-atomic="false"
            >
              {items.map((slide, i) => {
                const active = i === index;
                return (
                  <div
                    key={i}
                    className={`col-start-1 row-start-1 ${SLIDE_FADE} ${
                      active ? 'opacity-100' : 'pointer-events-none opacity-0'
                    }`}
                    {...(isCarousel
                      ? {
                          'aria-roledescription': 'slide',
                          'aria-label': `${i + 1} of ${items.length}`,
                          'aria-hidden': !active,
                          inert: !active,
                        }
                      : {})}
                  >
                    {slide.eyebrow && (
                      <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/15 px-4 py-2 font-cta text-xs font-semibold uppercase tracking-wider text-foreground">
                        {slide.eyebrow}
                      </span>
                    )}

                    {/*
                  Only the visible slide is an <h1>. The inactive ones are inert
                  and aria-hidden so assistive tech already sees a single
                  heading, but four <h1> elements in the DOM is still wrong for
                  a crawler  - so the tag itself switches with the slide.
                */}
                    <Heading
                      as={active ? 'h1' : 'p'}
                      className="mb-5 font-headline text-[1.75rem] font-extrabold leading-[1.12] tracking-tight text-primary sm:text-4xl lg:text-5xl"
                    >
                      {slide.title}
                      {slide.subtitle && (
                        <>
                          {' '}
                          <span className="text-accent">{slide.subtitle}</span>
                        </>
                      )}
                    </Heading>

                    {slide.description && (
                      <p className="max-w-xl font-body text-base leading-relaxed text-muted-foreground sm:text-lg">
                        {slide.description}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Buttons commented for Future release */}
            {/* <div className="flex flex-wrap justify-center lg:justify-start gap-4 mb-12">
          {primaryButtonHref.length > 1 && (
            <Link
              href={primaryButtonHref}
              className="group px-6 py-3 bg-primary text-primary-foreground font-cta font-semibold text-lg rounded-xl shadow-subtle transition duration-200 ease-organic hover:bg-primary/90 hover:shadow-dramatic hover:-translate-y-1 active:scale-[0.98] active:translate-y-0 flex items-center gap-2"
            >
              {primaryButtonText}
              <Icon
                name="ArrowRightIcon"
                size={18}
                className="transition-transform duration-200 ease-organic group-hover:translate-x-1"
              />
            </Link>
          )}

          {secondaryButtonHref.length > 1 && (
            <Link
              href={secondaryButtonHref}
              className="group px-6 py-3 bg-surface text-accent border-2 border-secondary font-cta font-semibold text-lg rounded-xl shadow-subtle transition duration-200 ease-organic hover:bg-secondary hover:text-secondary-foreground hover:border-secondary hover:shadow-organic hover:-translate-y-1 active:scale-[0.98] active:translate-y-0 flex items-center gap-2"
            >
              {secondaryButtonText}
              <Icon
                name="ArrowRightIcon"
                size={18}
                className="transition-transform duration-200 ease-organic group-hover:translate-x-1"
              />
            </Link>
          )}
        </div> */}

            {/*
          Strip row: the feature ticks and the two arrows share one line, so the
          controls read as part of the hero's furniture rather than floating
          above it. Dots and the visible pause button are gone at the client's
          request  - arrows are the only visible controls.
        */}
            {((features && features.length > 0) || isCarousel) && (
              <div className="mt-8 flex flex-wrap items-center justify-between gap-x-6 gap-y-5 border-t border-border/60 pt-6">
                {/*
            Two-line labels are expected ("Compliance led / Supplier"), so
            every cell is top-aligned  - the ticks line up on the first line
            instead of drifting with each label's height  - and `items-center`
            on the row keeps the arrows centred against the band either way.
            */}
                {features && features.length > 0 && (
                  <ul className="grid basis-full grid-cols-2 items-start gap-x-5 gap-y-4 sm:min-w-0 sm:basis-0 sm:grow sm:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3">
                    {features.map((signal) => (
                      <li key={signal.label} className="flex items-start gap-2.5">
                        <Icon
                          name="CheckCircleIcon"
                          size={18}
                          className="mt-0.5 shrink-0 text-primary"
                          aria-hidden="true"
                        />
                        <span className="font-body text-sm leading-snug text-balance text-foreground">
                          {signal.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {isCarousel && (
                  <div className="flex shrink-0 items-center gap-2">
                    <button type="button" onClick={takeControl(prev)} className={controlBtn}>
                      <Icon name="ArrowLeftIcon" size={18} aria-hidden="true" />
                      <span className="sr-only">Previous slide</span>
                    </button>
                    <button type="button" onClick={takeControl(next)} className={controlBtn}>
                      <Icon name="ArrowRightIcon" size={18} aria-hidden="true" />
                      <span className="sr-only">Next slide</span>
                    </button>

                    {/*
                  WCAG 2.2.2 still needs a way to stop auto-rotation. The visible
                  pause button was dropped, so this keyboard-reachable one stands
                  in: mouse users get pause-on-hover, keyboard users get this.
                  Remove it only if autoplay itself is removed.
                */}
                    {!reducedMotion && (
                      <button
                        type="button"
                        onClick={() => {
                          setInteracted(isPlaying);
                          setIsPlaying((prevPlaying) => !prevPlaying);
                        }}
                        className="sr-only focus:not-sr-only focus:flex focus:h-11 focus:w-auto focus:items-center focus:rounded-full focus:border focus:border-border focus:bg-surface focus:px-4 focus:font-cta focus:text-sm focus:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                      >
                        {isPlaying ? 'Pause slideshow' : 'Play slideshow'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div
            className="relative h-[38vh] min-h-[16rem] w-full overflow-hidden rounded-3xl sm:h-[46vh] lg:h-[32rem]"
            onTouchStart={isCarousel ? onTouchStart : undefined}
            onTouchEnd={isCarousel ? onTouchEnd : undefined}
          >
            {/*
          Images cross-fade with the text. Stacked absolutely so the frame never
          collapses mid-transition. Only the first is `priority` - it is the LCP
          candidate; eagerly loading all four would compete with it.
        */}
            {items.map((slide, i) => {
              const active = i === index;
              return (
                <div
                  key={i}
                  aria-hidden={isCarousel && !active}
                  className={`absolute inset-0 ${SLIDE_FADE} ${
                    active ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  {shouldMount(i) && (
                    <AppImage
                      src={slide.imageSrc ?? imageSrc}
                      alt={slide.imageAlt ?? imageAlt}
                      fill
                      // Only the first slide is the LCP candidate; fetching the
                      // rest eagerly would compete with it for bandwidth.
                      priority={i === 0}
                      loading={i === 0 ? undefined : 'lazy'}
                      sizes="(max-width: 1024px) 100vw, 50vw"
                      className="h-full w-full object-cover object-center"
                    />
                  )}
                </div>
              );
            })}
            {/* Subtle brand-tinted scrim to seat the image against the cream page */}
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-gradient-to-t from-foreground/10 via-transparent to-transparent"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
